'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { GitBranch, Phone, ExternalLink, AlertTriangle } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { formatDistanceToNow } from 'date-fns'

interface AgentFlow {
  id: string
  name: string
  is_active: boolean
  updated_at: string
  phone_numbers: string[]
}

interface PbxFlowsSectionProps {
  agentId: string
}

export function PbxFlowsSection({ agentId }: PbxFlowsSectionProps) {
  const { fetchWithAuth } = useAuth()
  const [flows, setFlows] = useState<AgentFlow[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    async function loadFlows() {
      try {
        const response = await fetchWithAuth(`/api/agents/voice/${agentId}/flows`)
        if (response.ok) {
          const data = await response.json()
          setFlows(data.flows || [])
        }
      } catch (error) {
        console.error('Error loading agent flows:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadFlows()
  }, [agentId, fetchWithAuth])

  const handleOpenFlow = (flowId: string) => {
    // Navigate to PBX page with flow editor
    window.location.href = `?page=pbx&flowId=${flowId}`
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-60 mt-1" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <GitBranch className="w-4 h-4" />
          <span>Used in Call Flows</span>
        </CardTitle>
        <CardDescription>
          {flows.length === 0
            ? 'This agent is not used in any call flows yet'
            : `This agent is used in ${flows.length} call flow${flows.length === 1 ? '' : 's'}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {flows.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <GitBranch className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">
              Add this agent to a call flow to start handling incoming calls
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => (window.location.href = '?page=pbx&tab=flows')}
            >
              <span>Go to Call Flows</span>
              <ExternalLink className="w-3 h-3 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {flows.length > 0 && (() => {
              const totalPhoneNumbers = flows.reduce((acc, f) => acc + f.phone_numbers.length, 0);
              return (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Changes to this agent will affect call handling for{' '}
                    {totalPhoneNumbers} phone number{totalPhoneNumbers === 1 ? '' : 's'}
                  </span>
                </div>
              );
            })()}

            {flows.map((flow) => (
              <div
                key={flow.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <GitBranch className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{flow.name}</span>
                      <Badge variant={flow.is_active ? 'default' : 'secondary'} className="text-xs">
                        {flow.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {flow.phone_numbers.length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Phone className="w-3 h-3" />
                        {flow.phone_numbers.join(', ')}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      <span>Updated</span> {formatDistanceToNow(new Date(flow.updated_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleOpenFlow(flow.id)}>
                  <span>Open</span>
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
